/* Courted · Search (Go out anywhere, DOM-26): Google's places near home, or in the town you're looking at, best rated
   first among places enough people have rated ("best restaurants in Toronto"). A result is the place's name, its
   rating and how many reviews, what it is and where, and Google's line about it; ♥ is your yes (kept with that town,
   like a swipe there), and tapping it opens the place. Results are shown, never kept. Each search is one call to
   Google's dearer tier, so it runs when you press search, not as you type. */
import React from 'react';
import * as H from './helpers.js';
import * as S from './sync.js';
import { Ico, Plate, Page, Google, track } from './ui.jsx';

const { useState, useEffect, useRef } = React;
// the last search, for coming back to it from a place you opened (the results come from sync's memory)
let LAST = { q: '', town: null };

export function SearchR({ q0, town, homeName, mine, theirs, partnerName, words, onBack, backLabel, onOpen, onAnswer, onWhere, onLookIn }) {
  const again = !q0 && LAST.q && LAST.town === (town ? town.id : null) ? LAST.q : '';
  q0 = q0 || again;
  const [q, setQ] = useState(q0 || '');
  const [res, setRes] = useState(null);       // null: nothing asked yet; 'busy'; 'error'; or the results
  const [asked, setAsked] = useState('');
  const field = useRef(null);
  const where = town ? town.name : homeName || 'home';
  const run = async (term) => {
    const t = String(term || '').trim();
    if (t.length < 2) return;
    setAsked(t);
    LAST = { q: t, town: town ? town.id : null };
    setRes('busy');
    try {
      const list = await S.searchPlaces(t, town && town.id);
      setRes(list);
      track('courted_searched', { found: list.length, away: !!town });
    } catch (e) { setRes('error'); }
  };
  // the first search, and the same search again when you change where you're looking
  useEffect(() => { const t = asked || q0; if (t) run(t); else try { field.current && field.current.focus({ preventScroll: true }); } catch (e) {} }, [town && town.id]);
  const named = H.namedTown(asked);
  const lookIn = named && (!town || town.name.toLowerCase() !== named.toLowerCase()) ? named : null;
  const list = Array.isArray(res) ? res : [];
  return (
    <Page onBack={onBack} back={backLabel} label="Search places" className="co-search">
      <div className="co-search-body">
        <form className="co-search-form" role="search" onSubmit={(e) => { e.preventDefault(); run(q); if (field.current) field.current.blur(); }}>
          <label className="dv-field">
            <Ico n="i-search" s />
            <input ref={field} type="search" value={q} onChange={(e) => setQ(e.target.value)} enterKeyHint="search"
              placeholder={town ? 'Search ' + town.name : 'Search near ' + where} aria-label={town ? 'Search ' + town.name : 'Search near ' + where} />
          </label>
        </form>
        <button type="button" className="co-where-btn co-search-where" onClick={onWhere}><Ico n={town ? 'i-pin' : 'i-house'} s /><span>{town ? 'In ' + H.townWords(town) : 'Near ' + where}</span><Ico n="i-chevron-down" s /></button>

        {res === 'busy' ? <p className="co-wait">Looking…</p> : null}
        {res === 'error' ? (
          <div className="co-empty co-msg"><h2 className="co-display">Couldn’t search just now</h2><p>Google didn’t answer. It usually does on a second go.</p>
            <button type="button" className="dv-btn dv-btn-pop" onClick={() => run(asked)}>Try again</button></div>
        ) : null}
        {Array.isArray(res) && !list.length ? (
          <div className="co-empty co-msg"><h2 className="co-display">Nothing for “{asked}” {town ? 'in ' + town.name : 'near ' + where}</h2>
            <p>{lookIn ? 'You’re looking ' + (town ? 'in ' + town.name : 'near ' + where) + '. Look in ' + lookIn + ' instead, and its yeses stay with it.' : 'Somewhere else? Change where you’re looking, and search there.'}</p>
            {lookIn && onLookIn ? <button type="button" className="dv-btn dv-btn-pop" onClick={() => onLookIn(lookIn)}>Look in {lookIn}</button> : null}
            <button type="button" className={'dv-btn ' + (lookIn ? 'dv-btn-plain' : 'dv-btn-soft')} onClick={onWhere}>Change where</button></div>
        ) : null}
        {list.length ? (
          <>
            <p className="dv-note co-search-note">Best rated first, among places with over 200 reviews.</p>
            <div className="dv-list co-results">
              {list.map((c, i) => {
                const kind = H.kindOf(c.id, null, c, null);
                const yes = H.yesLike(mine[c.id]), theirYes = H.yesLike(theirs[c.id]);
                return (
                  <div key={c.id} className="dv-row co-result">
                    <button type="button" className="co-result-open" onClick={() => onOpen(c.id)}>
                      <Plate card={c} kind={kind} load={i < 4} className="th" />
                      <span className="dv-row-text">
                        <b>{c.name}</b>
                        <small>{c.rating ? <span className="co-rating"><Ico n="i-star-f" s />{H.ratingWords(c)}</span> : null}{[H.whatOf(c, null, words), H.areaOf(c.address)].filter(Boolean).join(' · ')}</small>
                        {theirYes && partnerName ? <small><span className="dv-tag co-yes"><Ico n="i-heart-f" s />{partnerName} said yes</span></small>
                          : c.summary ? <small className="co-result-line">{c.summary}</small> : null}
                      </span>
                    </button>
                    <button type="button" className={'co-heart' + (yes ? ' on' : '')} aria-pressed={yes ? 'true' : 'false'} aria-label={(yes ? 'Not for us after all: ' : 'I’d go: ') + c.name}
                      onClick={() => onAnswer(c.id, kind, yes ? 'no' : 'yes')}>
                      <Ico n={yes ? 'i-heart-f' : 'i-heart'} />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        <Google />
      </div>
    </Page>
  );
}
